import { useEffect, useRef } from 'react';

// Lightweight canvas-based "3D-feel" particle orb — fast, no heavy libs.
export default function ThreeOrb() {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = ref.current!;
    const ctx = canvas.getContext('2d')!;
    let raf = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const { clientWidth: w, clientHeight: h } = canvas;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const N = window.innerWidth < 768 ? 90 : 180;
    const points = Array.from({ length: N }, () => {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      return { theta, phi, r: 1, s: 0.6 + Math.random() * 0.8 };
    });

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current.x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      mouse.current.y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);

    let t = 0;
    const render = () => {
      t += 0.003;
      const w = canvas.clientWidth, h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2, cy = h / 2;
      const R = Math.min(w, h) * 0.36;

      const ry = t + mouse.current.x * 0.6;
      const rx = t * 0.7 + mouse.current.y * 0.4;

      const projected = points.map((p) => {
        // spherical → cartesian
        let x = Math.sin(p.phi) * Math.cos(p.theta);
        let y = Math.cos(p.phi);
        let z = Math.sin(p.phi) * Math.sin(p.theta);
        // rotate Y
        let x1 = x * Math.cos(ry) + z * Math.sin(ry);
        let z1 = -x * Math.sin(ry) + z * Math.cos(ry);
        // rotate X
        let y2 = y * Math.cos(rx) - z1 * Math.sin(rx);
        let z2 = y * Math.sin(rx) + z1 * Math.cos(rx);
        const persp = 1.6 / (1.6 + z2);
        return { x: cx + x1 * R * persp, y: cy + y2 * R * persp, z: z2, s: p.s };
      });

      // Glow center
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 1.4);
      grad.addColorStop(0, 'rgba(139,92,246,0.35)');
      grad.addColorStop(0.6, 'rgba(124,58,237,0.06)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.beginPath(); ctx.arc(cx, cy, R * 1.4, 0, Math.PI * 2); ctx.fill();

      // Connections
      ctx.lineWidth = 0.6;
      for (let i = 0; i < projected.length; i++) {
        for (let j = i + 1; j < projected.length; j++) {
          const a = projected[i], b = projected[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 70) {
            const alpha = (1 - d / 70) * 0.35 * ((a.z + b.z) / 2 + 1) * 0.5;
            ctx.strokeStyle = `rgba(169,123,255,${alpha})`;
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          }
        }
      }

      // Points
      projected.forEach((p) => {
        const size = (p.z + 1.5) * p.s;
        ctx.fillStyle = `rgba(216,198,255,${0.55 + p.z * 0.3})`;
        ctx.beginPath(); ctx.arc(p.x, p.y, size, 0, Math.PI * 2); ctx.fill();
      });

      raf = requestAnimationFrame(render);
    };
    render();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); window.removeEventListener('mousemove', onMove); };
  }, []);

  return <canvas ref={ref} className="w-full h-full block" />;
}